/**
 * The platform's admin area — contact messages and how the service is doing. Served on the admin
 * subdomain (devadmin.<domain>); signed in with the one fixed administrator account, whose token
 * lives only in this tab's session storage and is sent as X-Admin-Token.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { LogOut, Mail, MailOpen, RefreshCw, Trash2 } from 'lucide-react'
import { API_BASE } from '../lib/api'
import { useI18n } from '../lib/i18n'
import Logo from '../components/Logo'
import { ErrorBanner } from '../components/primitives'

const TOKEN_KEY = 'devpulse.adminToken'

interface Overview {
  users: number
  organizations: number
  developers: number
  organizationsByPlan: Record<string, number>
  signupsLast7Days: number
  signupsLast30Days: number
  workSessionsLast7Days: number
  workOrdersLast7Days: number
  contactMessages: number
  unreadContactMessages: number
}

interface Message {
  id: string
  name: string
  email: string
  message: string
  source: string | null
  signedIn: boolean
  createdAt: string
  readAt: string | null
}

interface OrganizationRow {
  id: string
  name: string
  slug: string
  plan: string
  isDemo: boolean
  members: number
  createdAt: string
}

/** Every organization, and the one thing the platform may do to one: delete it whole (docs/refactor/65). */
function OrganizationsSection({ call }: { call: <T,>(path: string, init?: RequestInit) => Promise<T | null> }) {
  const { t, locale } = useI18n()
  const [rows, setRows] = useState<OrganizationRow[]>([])
  const [note, setNote] = useState<string | null>(null)
  const load = useCallback(async () => setRows((await call<OrganizationRow[]>('/organizations')) ?? []), [call])
  useEffect(() => {
    void load()
  }, [load])

  const remove = async (row: OrganizationRow): Promise<void> => {
    if (!window.confirm(t('admin.orgs.confirm', { name: row.name }))) return
    try {
      const result = await call<{ deletedRows: number }>(`/organizations/${row.id}`, { method: 'DELETE' })
      setNote(t('admin.orgs.deleted', { rows: result?.deletedRows ?? 0 }))
    } catch (failure) {
      setNote(failure instanceof Error ? failure.message : t('error.generic'))
    }
    await load()
  }

  return (
    <>
      <h2 className="small" style={{ margin: 0 }}>{t('admin.orgs.title')} ({rows.length})</h2>
      {note && <p className="small muted" style={{ margin: 0 }}>{note}</p>}
      <div className="repo-list">
        {rows.map((row) => (
          <article key={row.id} className="panel admin__message">
            <div className="row row--between row--wrap">
              <strong className="small">{row.name} <span className="muted">· {row.slug} · {row.plan}{row.isDemo ? ' · demo' : ''}</span></strong>
              <span className="tiny muted">{t('admin.orgs.members', { count: row.members })} · {new Date(row.createdAt).toLocaleDateString(locale)}</span>
            </div>
            <button type="button" className="btn btn--sm btn--danger" style={{ alignSelf: 'flex-start' }} onClick={() => void remove(row)}>
              <Trash2 size={13} aria-hidden /> {t('admin.orgs.delete')}
            </button>
          </article>
        ))}
      </div>
    </>
  )
}

function readToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export default function Admin() {
  const { t, locale } = useI18n()
  const [token, setToken] = useState<string | null>(readToken)
  const [user, setUser] = useState('')
  const [password, setPassword] = useState('')
  const [problem, setProblem] = useState<string | null>(null)
  const [overview, setOverview] = useState<Overview | null>(null)
  const [messages, setMessages] = useState<Message[]>([])

  const call = useCallback(
    async <T,>(path: string, init: RequestInit = {}): Promise<T | null> => {
      const response = await fetch(`${API_BASE}/admin${path}`, {
        ...init,
        headers: { 'Content-Type': 'application/json', ...(token ? { 'X-Admin-Token': token } : {}) },
      })
      if (response.status === 401) {
        try {
          sessionStorage.removeItem(TOKEN_KEY)
        } catch {
          // nothing kept
        }
        setToken(null)
        return null
      }
      if (!response.ok) throw new Error(String(response.status))
      return response.status === 204 ? null : ((await response.json()) as T)
    },
    [token],
  )

  const load = useCallback(async () => {
    setOverview(await call<Overview>('/overview'))
    setMessages((await call<Message[]>('/messages')) ?? [])
  }, [call])

  useEffect(() => {
    if (token) void load().catch(() => setProblem(t('error.generic')))
  }, [token, load, t])

  const signIn = async (event: FormEvent) => {
    event.preventDefault()
    setProblem(null)
    const response = await fetch(`${API_BASE}/admin/sign-in`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user, password }),
    })
    if (!response.ok) {
      setProblem(t('admin.wrong'))
      return
    }
    const { token: issued } = (await response.json()) as { token: string }
    try {
      sessionStorage.setItem(TOKEN_KEY, issued)
    } catch {
      // held for this page only
    }
    setPassword('')
    setToken(issued)
  }

  if (!token) {
    return (
      <div className="auth">
        <form className="auth__card stack" onSubmit={(e) => void signIn(e)}>
          <div className="row row--tight"><Logo size={28} /> <strong>{t('admin.title')}</strong></div>
          <label className="field">
            <span className="field__label">{t('admin.user')}</span>
            <input className="input" value={user} onChange={(e) => setUser(e.target.value)} autoComplete="username" />
          </label>
          <label className="field">
            <span className="field__label">{t('admin.password')}</span>
            <input className="input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
          </label>
          {problem && <ErrorBanner message={problem} />}
          <button type="submit" className="btn btn--primary">{t('admin.signIn')}</button>
        </form>
      </div>
    )
  }

  const stats: Array<[string, number | undefined]> = [
    ['admin.users', overview?.users],
    ['admin.organizations', overview?.organizations],
    ['admin.developers', overview?.developers],
    ['admin.signups7', overview?.signupsLast7Days],
    ['admin.signups30', overview?.signupsLast30Days],
    ['admin.sessions7', overview?.workSessionsLast7Days],
    ['admin.orders7', overview?.workOrdersLast7Days],
    ['admin.unread', overview?.unreadContactMessages],
  ]

  return (
    <div className="admin stack">
      <header className="row row--between">
        <div className="row row--tight"><Logo size={26} /> <strong>{t('admin.title')}</strong></div>
        <div className="row row--tight">
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => void load()}><RefreshCw size={13} /> {t('explorer.reload')}</button>
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => {
            try {
              sessionStorage.removeItem(TOKEN_KEY)
            } catch {
              // nothing kept
            }
            setToken(null)
          }}><LogOut size={13} /> {t('admin.signOut')}</button>
        </div>
      </header>

      <div className="admin__stats">
        {stats.map(([key, value]) => (
          <div key={key} className="panel admin__stat">
            <span className="tiny muted">{t(key)}</span>
            <strong>{value ?? '—'}</strong>
          </div>
        ))}
      </div>
      {overview && (
        <p className="small muted" style={{ margin: 0 }}>
          {Object.entries(overview.organizationsByPlan).map(([plan, count]) => `${plan}: ${count}`).join(' · ')}
        </p>
      )}

      <h2 className="small" style={{ margin: 0 }}>{t('admin.inbox')}</h2>
      {messages.length === 0 && <p className="small muted">{t('admin.noMessages')}</p>}
      <div className="repo-list">
        {messages.map((m) => (
          <article key={m.id} className={`panel admin__message${m.readAt ? '' : ' is-unread'}`}>
            <div className="row row--between row--wrap">
              <strong className="small row row--tight">{m.readAt ? <MailOpen size={13} /> : <Mail size={13} />} {m.name} · <a href={`mailto:${m.email}`}>{m.email}</a></strong>
              <span className="tiny muted">{new Date(m.createdAt).toLocaleString(locale)} · {m.source ?? '—'}{m.signedIn ? ` · ${t('admin.signedIn')}` : ''}</span>
            </div>
            <p className="small" style={{ whiteSpace: 'pre-wrap', margin: 0 }}>{m.message}</p>
            {!m.readAt && (
              <button type="button" className="btn btn--sm btn--ghost" style={{ alignSelf: 'flex-start' }}
                onClick={() => void call(`/messages/${m.id}/read`, { method: 'POST' }).then(() => load())}>
                {t('admin.markRead')}
              </button>
            )}
          </article>
        ))}
      </div>

      <OrganizationsSection call={call} />
    </div>
  )
}
